import { UniqueEntityId } from "@kernel/ddd";
import {
  IWebsocket,
  Websocket,
  WebsocketEvents
} from "@kernel/frontend-react-app";
import {
  SignalingMessageType,
  SignalingMessageDisconnect,
  SignalingMessage,
  SignalingMessageConnectReply,
  SignalingMessageOffer,
  SignalingMessageAnswer,
  SignalingMessageCandidate,
  SignalingMessageConnect,
  SignalingMessageUserlist,
  SignalingMessageChangedUsername,
  SignalingMessageChangeUsername
} from "./SignalingMessage";
import {
  MAX_MESSAGE_BYTES,
  validateSignalingMessage
} from "./SignalingMessageValidator";
import { LocalParticipant } from "@domain";

export class SignalingService extends EventTarget {
  constructor(
    private ws: Websocket,
    private interval = 1000
  ) {
    super();

    this.ws.addEventListener(
      WebsocketEvents.MESSAGE,
      (ws: IWebsocket, event: MessageEvent<string>) => {
        if (typeof event.data !== "string") return;
        if (event.data.length > MAX_MESSAGE_BYTES) {
          console.warn("Signaling message exceeds max bytes");
          return;
        }
        let parsed: unknown;
        try {
          parsed = JSON.parse(event.data);
        } catch (error) {
          console.warn("Failed to parse signaling message", error);
          return;
        }
        let msg: SignalingMessage;
        try {
          msg = validateSignalingMessage(parsed);
        } catch (error) {
          console.warn("Invalid signaling message", error);
          return;
        }
        switch (msg.type) {
          case SignalingMessageType.CONNECT:
            return this.handleConnect(msg as SignalingMessageConnectReply);
          case SignalingMessageType.USERLIST:
            return this.handleRefreshUserList(msg as SignalingMessageUserlist);
          case SignalingMessageType.CHANGE_USERNAME:
            return this.handleChangedUsername(
              msg as SignalingMessageChangedUsername
            );
          case SignalingMessageType.DISCONNECT:
            return this.handleDisconnect(msg as SignalingMessageDisconnect);
          case SignalingMessageType.OFFER:
            return this.handleOffer(msg as SignalingMessageOffer);
          case SignalingMessageType.ANSWER:
            return this.handleAnswer(msg as SignalingMessageAnswer);
          case SignalingMessageType.CANDIDATE:
            return this.handleCandidate(msg as SignalingMessageCandidate);
          default:
            return;
        }
      }
    );
  }

  public createConnection(
    room: UniqueEntityId,
    username: string,
    type: string
  ) {
    const message: SignalingMessageConnect = {
      type: SignalingMessageType.CONNECT,
      data: {
        username,
        type,
        room: room.toString()
      }
    };
    const sendJson = JSON.stringify(message);
    this.ws.send(sendJson);
  }

  public setUsername(room: UniqueEntityId, username: string, type: string) {
    const message: SignalingMessageConnect = {
      type: SignalingMessageType.USERNAME,
      data: {
        username,
        type,
        room: room.toString()
      }
    };
    const sendJson = JSON.stringify(message);
    this.ws.send(sendJson);
  }

  public changeUsername(
    room: UniqueEntityId,
    username: string,
    remoteId: UniqueEntityId
  ) {
    const message: SignalingMessageChangeUsername = {
      type: SignalingMessageType.CHANGE_USERNAME,
      data: {
        username,
        room: room.toString(),
        date: Date.now(),
        id: remoteId.toValue() as number
      }
    };
    const sendJson = JSON.stringify(message);
    this.ws.send(sendJson);
  }

  public deleteConnection(room: UniqueEntityId, local: LocalParticipant) {
    const message: SignalingMessageDisconnect = {
      type: SignalingMessageType.DISCONNECT,
      data: {
        user: {
          id: local.remoteId!.toValue() as number,
          username: local.displayName,
          type: local.type
        },
        room: room.toString()
      }
    };
    const sendJson = JSON.stringify(message);
    this.ws.send(sendJson);
  }

  public sendOffer(
    room: UniqueEntityId,
    description: RTCSessionDescription | null,
    from?: number,
    to?: number
  ) {
    if (!description) return;
    const message: SignalingMessageOffer = {
      type: SignalingMessageType.OFFER,
      from,
      to,
      data: {
        sdp: description.sdp,
        datetime: Date.now(),
        room: room.toString()
      }
    };
    const sendJson = JSON.stringify(message);
    this.ws.send(sendJson);
  }

  public sendAnswer(
    room: UniqueEntityId,
    description: RTCSessionDescription | null,
    from?: number,
    to?: number
  ) {
    if (!description) return;
    const message: SignalingMessageAnswer = {
      type: SignalingMessageType.ANSWER,
      from,
      to,
      data: {
        sdp: description.sdp,
        datetime: Date.now(),
        room: room.toString()
      }
    };
    const sendJson = JSON.stringify(message);
    this.ws.send(sendJson);
  }

  public sendCandidate(
    room: UniqueEntityId,
    candidate: RTCIceCandidate,
    from?: number,
    to?: number
  ) {
    const message: SignalingMessageCandidate = {
      type: SignalingMessageType.CANDIDATE,
      from,
      to,
      data: {
        candidate: candidate.candidate,
        sdpMLineIndex: candidate.sdpMLineIndex!,
        sdpMid: candidate.sdpMid!,
        datetime: Date.now(),
        room: room.toString()
      }
    };
    const sendJson = JSON.stringify(message);
    this.ws.send(sendJson);
  }

  private handleConnect(msg: SignalingMessageConnectReply) {
    this.dispatchEvent(
      new CustomEvent<SignalingMessageConnectReply>(
        SignalingMessageType.CONNECT,
        {
          detail: msg
        }
      )
    );
  }

  private handleDisconnect(msg: SignalingMessageDisconnect) {
    this.dispatchEvent(
      new CustomEvent<SignalingMessageDisconnect>(
        SignalingMessageType.DISCONNECT,
        {
          detail: msg
        }
      )
    );
  }

  private handleOffer(msg: SignalingMessageOffer) {
    this.dispatchEvent(
      new CustomEvent<SignalingMessageOffer>(SignalingMessageType.OFFER, {
        detail: msg
      })
    );
  }

  private handleAnswer(msg: SignalingMessageAnswer) {
    this.dispatchEvent(
      new CustomEvent<SignalingMessageAnswer>(SignalingMessageType.ANSWER, {
        detail: msg
      })
    );
  }

  private handleCandidate(msg: SignalingMessageCandidate) {
    this.dispatchEvent(
      new CustomEvent<SignalingMessageCandidate>(
        SignalingMessageType.CANDIDATE,
        {
          detail: msg
        }
      )
    );
  }

  private handleRefreshUserList(msg: SignalingMessageUserlist) {
    this.dispatchEvent(
      new CustomEvent<SignalingMessageUserlist>(SignalingMessageType.USERLIST, {
        detail: msg
      })
    );
  }

  private handleChangedUsername(msg: SignalingMessageChangedUsername) {
    this.dispatchEvent(
      new CustomEvent<SignalingMessageChangedUsername>(
        SignalingMessageType.CHANGE_USERNAME,
        {
          detail: msg
        }
      )
    );
  }
}
